import { Ref } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { Controller, FieldValues, useFormContext } from "react-hook-form";

import { FormFeedback } from "@/components/Form/Base/formFeedback.component";
import { FormGroup } from "@/components/Form/Base/formGroup.component";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import {
    BreakpointContext,
    BreakpointProvider
} from "@/utils/breakpoints/breakpoints.provider";
import { Config } from "@/utils/config.utils";

import { FormValuesExploded } from "../formField.types";

type RecaptchaFieldProps<T extends FieldValues> = Pick<
    FormValuesExploded<T>,
    "name"
> & {
    ref: Ref<ReCAPTCHA | null>;
};

const RecaptchaField = <T extends FieldValues>({
    ref,
    name
}: RecaptchaFieldProps<T>) => {
    const { t } = useGranite();

    const { control } = useFormContext<T>();

    return (
        <FormGroup>
            <Controller
                control={control}
                name={name}
                render={({ field, fieldState }) => {
                    return (
                        <div>
                            <BreakpointProvider>
                                <BreakpointContext.Consumer>
                                    {(contextValues) => {
                                        return (
                                            <ReCAPTCHA
                                                ref={ref}
                                                size={
                                                    contextValues?.breakpoints
                                                        .sm
                                                        ? "compact"
                                                        : "normal"
                                                }
                                                sitekey={
                                                    Config.integrations[
                                                        "google-captcha-site-key"
                                                    ]
                                                }
                                                hl={Config.getLanguage()}
                                                onChange={field.onChange}
                                            />
                                        );
                                    }}
                                </BreakpointContext.Consumer>
                            </BreakpointProvider>
                            {fieldState.isTouched && !!fieldState.error && (
                                <FormFeedback className={"d-block"}>
                                    {fieldState.error.message()(t)}
                                </FormFeedback>
                            )}
                        </div>
                    );
                }}
            />
        </FormGroup>
    );
};

export { RecaptchaField };
